/**
 * PopPage Component
 *
 * Main page for viewing and editing Personal Development Plans.
 * Supports both employee view (own POP) and manager view (edit + progress).
 * Story 6-5
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../auth';
import { usePop, useUpdatePop, useAddProgressEntry, useDeleteProgressEntry } from './usePop';
import { PopViewer } from './PopViewer';
import { PopEditor } from './PopEditor';
import { ProgressTimeline } from './ProgressTimeline';
import { ProgressEntryForm } from './ProgressEntryForm';

export function PopPage() {
  const { employeeId: employeeIdParam } = useParams<{ employeeId: string }>();
  const { user } = useAuth();

  // Determine employee ID - from URL param or current user's employee profile
  const employeeId = employeeIdParam ? parseInt(employeeIdParam) : user?.employeeId || 0;

  const { data: pop, isLoading, error } = usePop(employeeId);
  const updatePop = useUpdatePop();
  const addProgressEntry = useAddProgressEntry();
  const deleteProgressEntry = useDeleteProgressEntry();

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');

  const isManager = user?.role === 'beoordelaar' || user?.role === 'hr' || user?.role === 'admin';
  const canEdit = isManager;
  const canAddProgress = isManager || user?.employeeId === employeeId;

  const handleStartEdit = () => {
    setEditContent(pop?.content || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent('');
  };

  const handleSave = async () => {
    if (!employeeId) return;

    await updatePop.mutateAsync({
      employeeId,
      input: { content: editContent },
    });

    setIsEditing(false);
  };

  const handleAddProgress = async (content: string, progressDate?: string) => {
    if (!employeeId) return;

    await addProgressEntry.mutateAsync({
      employeeId,
      input: { content, progressDate },
    });
  };

  const handleDeleteProgress = async (progressId: number) => {
    if (!employeeId) return;

    await deleteProgressEntry.mutateAsync({
      employeeId,
      progressId,
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">POP laden...</span>
        </div>
      </Layout>
    );
  }

  // Show error only for real errors (404 is handled in usePop by returning null)
  if (error) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 rounded-lg border border-red-200 p-8 text-center">
            <h3 className="text-sm font-medium text-red-900">Fout bij laden POP</h3>
            <p className="mt-1 text-sm text-red-600">
              Er is een fout opgetreden bij het laden van het ontwikkelingsplan.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  // Show empty state with create option when no POP exists (and not editing)
  // pop === null means no POP exists (404 from API)
  if (!pop && !isEditing) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Geen POP gevonden</h3>
            <p className="mt-1 text-sm text-gray-500">
              Er is nog geen persoonlijk ontwikkelingsplan aangemaakt.
            </p>
            {canEdit && (
              <button
                onClick={handleStartEdit}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                POP Aanmaken
              </button>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  // Show editor when creating new POP (no existing pop, but isEditing is true)
  if (isEditing && !pop) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Nieuw Persoonlijk Ontwikkelingsplan</h1>
            <p className="mt-1 text-sm text-gray-500">
              Maak een nieuw ontwikkelingsplan aan voor deze medewerker.
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden p-6">
            <PopEditor
              content={editContent}
              onChange={setEditContent}
              onSave={handleSave}
              onCancel={handleCancelEdit}
              isSaving={updatePop.isPending}
            />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="pop-page" className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Persoonlijk Ontwikkelingsplan</h1>
            {pop && (
              <p className="mt-1 text-sm text-gray-500">
                Laatst bijgewerkt door {pop.lastUpdatedByName} op{' '}
                {new Date(pop.updatedAt).toLocaleDateString('nl-NL')}
              </p>
            )}
          </div>

          {canEdit && !isEditing && pop && (
            <button
              data-testid="edit-pop-button"
              onClick={handleStartEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Bewerken
            </button>
          )}
        </div>

        {/* Content Section */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {isEditing ? (
            <div className="p-6">
              <PopEditor
                content={editContent}
                onChange={setEditContent}
                onSave={handleSave}
                onCancel={handleCancelEdit}
                isSaving={updatePop.isPending}
              />
            </div>
          ) : pop ? (
            <div className="p-6">
              <PopViewer content={pop.content} />
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <p>Nog geen inhoud</p>
            </div>
          )}
        </div>

        {/* Progress Section */}
        {pop && !isEditing && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Voortgang</h2>

            {canAddProgress && (
              <div className="mb-6">
                <ProgressEntryForm
                  onSubmit={handleAddProgress}
                  isSubmitting={addProgressEntry.isPending}
                />
              </div>
            )}

            <ProgressTimeline
              entries={pop.progressEntries || []}
              canDelete={canEdit}
              onDelete={handleDeleteProgress}
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
